import React from 'react';
import { Link } from 'react-router-dom';
import { Wrench, Fan, Phone, CheckCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import useSEO from '../hooks/useSEO';

const Services = () => {
  const { t } = useLanguage();
  useSEO({
    title: 'Υπηρεσίες - Επισκευή Φούρνου, Φριτέζας, Απορροφητήρα | PrimeTech Santorini',
    description: 'Επαγγελματικές επισκευές: φούρνοι, εστίες, φριτέζες, πλυντήρια πιάτων, συστήματα εξαερισμού & inverter απορροφητήρων στη Σαντορίνη.',
  });

  const services = [
    {
      icon: <Wrench className="w-12 h-12" />,
      title: { el: 'Επισκευές Εξοπλισμού', en: 'Equipment Repairs' },
      image: 'https://customer-assets.emergentagent.com/job_829760ef-d5ce-4dba-ba84-41f6905396d9/artifacts/tg8pas0i_IMG_0036.jpeg',
      description: {
        el: 'Ολοκληρωμένες υπηρεσίες επισκευής για όλα τα είδη επαγγελματικού εξοπλισμού κουζίνας. Καθαρισμοί inverter, επισκευή φριτέζας και πλυντηρίων πιάτων.',
        en: 'Complete repair services for all types of commercial kitchen equipment. Inverter cleaning, fryer and dishwasher repair.'
      },
      features: [
        { el: 'Φούρνοι & Εστίες', en: 'Ovens & Stoves' },
        { el: 'Επισκευή Φριτέζας', en: 'Fryer Repair' },
        { el: 'Πλυντήρια Πιάτων', en: 'Dishwashers' },
        { el: 'Καθαρισμοί Inverter', en: 'Inverter Cleaning' },
      ]
    },
    {
      icon: <Fan className="w-12 h-12" />,
      title: { el: 'Συστήματα Εξαερισμού', en: 'Ventilation Systems' },
      image: 'https://customer-assets.emergentagent.com/job_829760ef-d5ce-4dba-ba84-41f6905396d9/artifacts/r3jgx38o_IMG_6422.heic',
      description: {
        el: 'Εγκατάσταση, συντήρηση και επισκευή συστημάτων εξαερισμού και απορροφητήρων. Τοποθέτηση inverter βάση τα κυβικά του μοτέρ. Βάζουμε επιπλέον και φίλτρα άνθρακα αν χρειαστεί με στάδια για να βγαίνει άοσμο ο καπνός από την κουζίνα και πληρεί όλες τις προδιαγραφές.',
        en: 'Installation, maintenance and repair of ventilation systems and extractors. Inverter installation based on motor capacity. We also install carbon filters with stages if needed for odor-free smoke extraction from the kitchen meeting all specifications.'
      },
      features: [
        { el: 'Απορροφητήρες (Φούσκες)', en: 'Kitchen Hoods' },
        { el: 'Τοποθέτηση Inverter βάση Κυβικά Μοτέρ', en: 'Inverter Installation by Motor Capacity' },
        { el: 'Φίλτρα Άνθρακα με Στάδια', en: 'Multi-Stage Carbon Filters' },
        { el: 'Καθαρισμός Συστημάτων', en: 'System Cleaning' },
      ]
    },
  ];

  return (
    <div className="pt-20">
      {/* Hero */}
      <section className="bg-slate-900 py-24" data-testid="services-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6">
            {t({ el: 'ΟΙ ΥΠΗΡΕΣΙΕΣ ΜΑΣ', en: 'OUR SERVICES' })}
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            {t({
              el: 'Εξειδικευμένες τεχνικές υπηρεσίες για επαγγελματικές κουζίνες στη Σαντορίνη',
              en: 'Specialized technical services for commercial kitchens in Santorini'
            })}
          </p>
        </div>
      </section>

      {/* Services Details */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-32 md:space-y-48">
            {services.map((service, index) => (
              <div 
                key={index}
                data-testid={`service-detail-${index}`}
                className="flex flex-col"
              >
                {/* Image on top */}
                <div className="mb-8">
                  <img
                    src={service.image}
                    alt={t(service.title)}
                    className="w-full h-[400px] object-cover shadow-2xl"
                  />
                </div>

                {/* Content below */}
                <div>
                  <div className="w-16 h-16 bg-orange-100 rounded-lg flex items-center justify-center mb-6 text-orange-600">
                    {service.icon}
                  </div>
                  <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mb-6">
                    {t(service.title)}
                  </h2>
                  <p className="text-lg text-slate-600 mb-8">
                    {t(service.description)}
                  </p>
                  
                  <div className="space-y-4 mb-10">
                    {service.features.map((feature, fIndex) => (
                      <div key={fIndex} className="flex items-center space-x-3">
                        <CheckCircle className="w-6 h-6 text-orange-500 flex-shrink-0" />
                        <span className="text-slate-700 font-medium">{t(feature)}</span>
                      </div>
                    ))}
                  </div>

                  <Link
                    to="/chat"
                    data-testid={`service-chat-btn-${index}`}
                    className="bg-orange-500 text-white px-8 py-4 font-bold tracking-wider hover:bg-orange-600 transition-colors inline-flex items-center space-x-2"
                  >
                    <Phone className="w-5 h-5" />
                    <span>{t({ el: 'ΖΗΤΗΣΤΕ ΒΟΗΘΕΙΑ', en: 'REQUEST HELP' })}</span>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-slate-50 py-24" data-testid="services-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mb-6">
            {t({ el: 'ΕΤΟΙΜΟΙ ΝΑ ΣΑΣ ΕΞΥΠΗΡΕΤΗΣΟΥΜΕ', en: 'READY TO SERVE YOU' })}
          </h2>
          <p className="text-lg text-slate-600 mb-10">
            {t({
              el: 'Επικοινωνήστε μαζί μας άμεσα μέσω του AI-powered συστήματός μας',
              en: 'Contact us instantly through our AI-powered system'
            })}
          </p>
          <Link
            to="/chat"
            data-testid="services-bottom-cta"
            className="bg-slate-900 text-white px-10 py-5 text-lg font-bold tracking-wider hover:bg-slate-800 transition-colors inline-flex items-center space-x-2"
          >
            <Phone className="w-6 h-6" />
            <span>{t({ el: 'ΞΕΚΙΝΗΣΤΕ ΣΥΝΟΜΙΛΙΑ', en: 'START CHAT' })}</span>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
